import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  DocumentTextIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { Button } from '@cottage/design-system/internal-tooling/ui'
import PdfViewer from '@/components/PdfViewer'
import AmazonChargePreview from '@/components/AmazonChargePreview'
import { getDocument } from '@/services/documents'
import { toLineItemHighlightData } from '@/utils/documentPanelPurchasing'
import { applyPurchasingVendorItemPackRuling } from '@/services/purchasing'
import type { PurchasingReviewQueueRow } from '@/services/purchasingTypes'
import { toast } from '@/store/toastStore'
import { formatApiError } from '@/utils/apiError'
import {
  buildPackRulingPayload,
  flagBadgeClass,
  isAliasPackLine,
  isPackDriftLine,
  purchaseLineSourceLabel,
  queueRowToPurchaseLine,
  reasonLabel,
} from './purchasingReviewTableConfig'
import {
  computeCostPerAtomicForDraft,
  usePurchaseLineAutosave,
} from './purchasingLineAutosave'
import PurchasingPackSelect from './PurchasingPackSelect'

function formatMoney(value: string | null | undefined): string {
  if (!value) return '—'
  const num = Number(value)
  if (!Number.isFinite(num)) return value
  return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })
}

function qboExpenseUrl(entityId: string): string {
  return `https://app.qbo.intuit.com/app/expense?txnId=${entityId}`
}

export type PurchasingSourceInvoiceModalProps = {
  open: boolean
  row: PurchasingReviewQueueRow
  onClose: () => void
  onSaved: (row: PurchasingReviewQueueRow) => void
  onPackRulingApplied: () => void
  renderReasonActions?: (row: PurchasingReviewQueueRow) => React.ReactNode
  decidedBy?: string | null
  decidedAt?: string | null
  onUndo?: () => void
  showUndo?: boolean
  productPicker?: ReactNode
}

export default function PurchasingSourceInvoiceModal({
  open,
  row,
  onClose,
  onSaved,
  onPackRulingApplied,
  renderReasonActions,
  decidedBy,
  decidedAt,
  onUndo,
  showUndo = false,
  productPicker,
}: PurchasingSourceInvoiceModalProps) {
  const lineId = row.id
  const documentId = row.document_id
  const modalRootRef = useRef<HTMLDivElement>(null)
  const [packBusy, setPackBusy] = useState(false)
  const [amazonExtracted, setAmazonExtracted] = useState<Record<string, unknown> | null>(null)

  const isAmazonReportLine = row.source === 'amazon_report'

  useEffect(() => {
    if (!open || !isAmazonReportLine || documentId == null) {
      setAmazonExtracted(null)
      return
    }
    let cancelled = false
    void getDocument(documentId)
      .then((doc) => {
        if (!cancelled) {
          setAmazonExtracted((doc.extracted_data as Record<string, unknown> | null) ?? null)
        }
      })
      .catch(() => {
        if (!cancelled) setAmazonExtracted(null)
      })
    return () => {
      cancelled = true
    }
  }, [documentId, isAmazonReportLine, open])

  const {
    draft,
    updateDraft,
    flushPersistAfterFieldCommit,
    handleClose,
    saving,
    lastSaved,
  } = usePurchaseLineAutosave({
    lineId,
    row,
    onSaved,
    onClose,
    modalRootRef,
  })

  const { vendorItemCode, description, packText, quantity, unitPrice, amount } = draft

  const purchaseLine = useMemo(() => queueRowToPurchaseLine(row), [row])
  const showPackContext = isPackDriftLine(purchaseLine) || isAliasPackLine(purchaseLine)
  const derivedCostPerAtomic = useMemo(
    () =>
      computeCostPerAtomicForDraft(draft, row.atomic_per_pack, row.cost_per_atomic_unit) ||
      row.cost_per_atomic_unit ||
      '',
    [draft, row.atomic_per_pack, row.cost_per_atomic_unit],
  )

  const highlightData = useMemo(
    () =>
      toLineItemHighlightData({
        amount: amount || row.extended_price,
        description,
        vendor_item_code: vendorItemCode,
      }),
    [amount, description, row.extended_price, vendorItemCode],
  )

  const handlePackRuling = useCallback(
    async (ruling: 'correction' | 'vendor_change') => {
      setPackBusy(true)
      try {
        await applyPurchasingVendorItemPackRuling(
          row.vendor_item_id,
          buildPackRulingPayload(ruling, purchaseLine, packText),
        )
        toast.success(ruling === 'correction' ? 'Pack corrected' : 'Pack change recorded')
        onPackRulingApplied()
      } catch (err) {
        toast.error(formatApiError(err, 'Failed to apply pack ruling'))
      } finally {
        setPackBusy(false)
      }
    },
    [onPackRulingApplied, packText, purchaseLine, row.vendor_item_id],
  )

  if (!open) return null

  const qboEntityId = row.qbo_entity_id

  return (
    <div className="fixed inset-0 z-50 flex flex-col" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} aria-hidden="true" />
      <div
        ref={modalRootRef}
        className="relative m-4 flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg bg-white shadow-xl"
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Review purchase line</h2>
            <p className="text-sm text-gray-500">
              {row.vendor_item_code}
              {row.description ? ` · ${row.description}` : ''}
              {row.source ? (
                <span className="ml-2 rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                  {purchaseLineSourceLabel(row.source)}
                </span>
              ) : null}
            </p>
            {decidedBy || decidedAt ? (
              <p className="text-xs text-gray-500">
                {decidedBy ? `Decided by ${decidedBy}` : null}
                {decidedBy && decidedAt ? ' · ' : null}
                {decidedAt ? decidedAt.slice(0, 10) : null}
              </p>
            ) : null}
          </div>
          <Button variant="ghost" iconOnly aria-label="Close" onClick={handleClose}>
            <XMarkIcon className="h-5 w-5" />
          </Button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
          <div className="flex min-h-[240px] flex-1 flex-col border-b border-gray-200 lg:border-b-0 lg:border-r">
            {documentId != null && isAmazonReportLine ? (
              <AmazonChargePreview extractedData={amazonExtracted} />
            ) : documentId != null ? (
              <PdfViewer
                documentId={documentId}
                highlightData={highlightData}
                pageNumber={row.page_number}
                bbox={
                  row.bbox as
                    | { x0: number; y0: number; x1: number; y1: number }
                    | number[]
                    | null
                    | undefined
                }
                toolbarBeforeDownload={
                  <button
                    type="button"
                    className="inline-flex items-center justify-center rounded-md p-1 hover:bg-gray-100 max-md:min-h-[44px] max-md:min-w-[44px]"
                    title="Open in Invoices page"
                    aria-label="Open in Invoices page"
                    onClick={() => {
                      window.location.assign(`/bookkeeper/doc-review?doc=${documentId}`)
                    }}
                  >
                    <DocumentTextIcon className="h-5 w-5" />
                  </button>
                }
              />
            ) : qboEntityId ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
                <p className="text-sm text-gray-600">No linked document — open this expense in QuickBooks.</p>
                <a
                  href={qboExpenseUrl(qboEntityId)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm font-medium text-brand-700 hover:text-brand-800"
                >
                  Open in QuickBooks
                  <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                </a>
              </div>
            ) : (
              <div className="flex flex-1 items-center justify-center p-8 text-sm text-gray-500">
                No source document available.
              </div>
            )}
          </div>

          <div className="w-full min-w-0 shrink-0 overflow-y-auto p-4 lg:w-96">
            <div className="space-y-3 text-sm">
              <label className="block">
                <span className="text-xs font-medium uppercase tracking-wide text-gray-500">Item code</span>
                <input
                  className="input mt-1 w-full"
                  value={vendorItemCode}
                  onChange={(e) => updateDraft({ vendorItemCode: e.target.value })}
                  onBlur={flushPersistAfterFieldCommit}
                />
              </label>
              <label className="block">
                <span className="text-xs font-medium uppercase tracking-wide text-gray-500">Description</span>
                <input
                  className="input mt-1 w-full"
                  value={description}
                  onChange={(e) => updateDraft({ description: e.target.value })}
                  onBlur={flushPersistAfterFieldCommit}
                />
              </label>
              <label className="block" htmlFor="review-modal-pack">
                <span className="text-xs font-medium uppercase tracking-wide text-gray-500">Pack</span>
                <div className="mt-1">
                  <PurchasingPackSelect
                    id="review-modal-pack"
                    value={packText}
                    onChange={(next) => updateDraft({ packText: next })}
                    onCommit={flushPersistAfterFieldCommit}
                    preferBelow
                  />
                </div>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs font-medium uppercase tracking-wide text-gray-500">Qty</span>
                  <input
                    className="input mt-1 w-full"
                    value={quantity}
                    onChange={(e) => updateDraft({ quantity: e.target.value })}
                    onBlur={flushPersistAfterFieldCommit}
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-medium uppercase tracking-wide text-gray-500">Unit $</span>
                  <input
                    className="input mt-1 w-full"
                    value={unitPrice}
                    onChange={(e) => updateDraft({ unitPrice: e.target.value })}
                    onBlur={flushPersistAfterFieldCommit}
                  />
                </label>
              </div>
              <label className="block">
                <span className="text-xs font-medium uppercase tracking-wide text-gray-500">Amount</span>
                <input
                  className="input mt-1 w-full"
                  value={amount}
                  onChange={(e) => updateDraft({ amount: e.target.value })}
                  onBlur={flushPersistAfterFieldCommit}
                />
              </label>
              {productPicker ? (
                <label className="block min-w-0">
                  <span className="text-xs font-medium uppercase tracking-wide text-gray-500">Product</span>
                  <div className="mt-1 min-w-0">{productPicker}</div>
                </label>
              ) : null}
              <div>
                <span className="text-xs font-medium uppercase tracking-wide text-gray-500">$/atomic</span>
                <p className="mt-1 text-gray-900">{formatMoney(derivedCostPerAtomic)}</p>
                {row.median_cost_per_atomic ? (
                  <p className="text-xs text-gray-500">Median: {formatMoney(row.median_cost_per_atomic)}</p>
                ) : null}
              </div>
              <div>
                <span className="text-xs font-medium uppercase tracking-wide text-gray-500">Reasons</span>
                <div className="mt-1 flex flex-wrap gap-1">
                  {(row.reasons ?? []).map((reason) => (
                    <span key={reason} className={flagBadgeClass(reason)}>
                      {reasonLabel(reason)}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {showPackContext ? (
              <div className="mt-4 space-y-2 border-t border-gray-200 pt-4">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Pack context</p>
                <div className="flex flex-wrap gap-2">
                  {isPackDriftLine(purchaseLine) ? (
                    <>
                      <Button variant="outline" size="sm" disabled={packBusy} onClick={() => void handlePackRuling('correction')}>
                        Pack was wrong
                      </Button>
                      <Button variant="outline" size="sm" disabled={packBusy} onClick={() => void handlePackRuling('vendor_change')}>
                        Vendor changed pack
                      </Button>
                    </>
                  ) : null}
                  {isAliasPackLine(purchaseLine) ? (
                    <Button variant="outline" size="sm" disabled={packBusy} onClick={() => void handlePackRuling('correction')}>
                      Confirm pack
                    </Button>
                  ) : null}
                </div>
              </div>
            ) : null}

            {(() => {
              const reasonActionContent = renderReasonActions?.(row)
              if (!reasonActionContent) return null
              return (
                <div className="mt-4 border-t border-gray-200 pt-4">
                  <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">
                    Flag actions
                  </p>
                  {reasonActionContent}
                </div>
              )
            })()}

            <div className="mt-6 flex flex-wrap items-center gap-2">
              {showUndo && onUndo ? (
                <Button variant="outline" onClick={onUndo}>
                  Undo
                </Button>
              ) : null}
              {saving ? (
                <span className="flex items-center gap-1 text-xs text-gray-500">
                  <ArrowPathIcon className="h-3 w-3 animate-spin" />
                  Saving...
                </span>
              ) : null}
              {!saving && lastSaved ? (
                <span className="text-xs text-gray-400">Auto-saved</span>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
